import React, { useState } from 'react';
import Button from '../../../components/base/Button';

const SpeakingSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    event_type: '',
    event_date: '',
    audience_size: '',
    topic: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const topics = [
    "The New Path to Wealth: Buy a Business, Don't Build One",
    "How Professionals Can Acquire Businesses with Minimal Capital", 
    "Preparing Your Business for Sale or Succession",
    "From Employee to Owner: Rewriting the Professional Playbook"
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d44i0lepguuav66rggrg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          organization: '',
          event_type: '',
          event_date: '',
          audience_size: '',
          topic: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="speaking" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Content */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6 font-serif">
              Speaking & <span className="text-yellow-600 italic">Media</span>
            </h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Glory Odudu speaks to professionals, business owners, and investors about the power of ownership and acquisition as the modern path to freedom and wealth.
            </p>

            {/* Speaking Image */}
            <div className="relative mb-8 rounded-lg overflow-hidden shadow-lg">
              <img
                src="https://static.readdy.ai/image/224dc00200a5f30b66b07d4513abc50a/c4a61b94da4487c2b8ef74f3eb9730b9.jpeg"
                alt="Glory Odudu Speaking"
                className="w-full h-64 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>

            {/* Topics */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-black mb-4 font-serif">Speaking Topics</h3>
              <div className="space-y-4">
                {topics.map((topic, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center mt-1 mr-4">
                      <i className="ri-mic-line text-yellow-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">{topic}</h4>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Speaking Inquiry Form */}
          <div className="bg-gray-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-black mb-6 font-serif">
              Invite Glory to Speak
            </h3>
            
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                Thank you! Your speaking inquiry has been submitted successfully.
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                There was an error submitting your inquiry. Please try again.
              </div>
            )}

            <form onSubmit={handleSubmit} data-readdy-form id="speaking-inquiry">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-600 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-600 text-sm"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                  Organization *
                </label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-600 text-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="event_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Event Type
                  </label>
                  <div className="relative">
                    <select
                      id="event_type"
                      name="event_type"
                      value={formData.event_type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-600 text-sm appearance-none"
                    >
                      <option value="">Select Event Type</option>
                      <option value="conference">Conference</option>
                      <option value="workshop">Workshop</option>
                      <option value="webinar">Webinar</option>
                      <option value="corporate">Corporate Event</option>
                      <option value="podcast">Podcast</option>
                      <option value="other">Other</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="event_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Event Date
                  </label>
                  <input
                    type="date"
                    id="event_date"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-600 text-sm"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="audience_size" className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Audience Size
                </label>
                <input
                  type="text"
                  id="audience_size"
                  name="audience_size"
                  value={formData.audience_size}
                  onChange={handleInputChange}
                  placeholder="e.g., 50-100 people"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-600 text-sm"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Topic
                </label>
                <div className="relative">
                  <select
                    id="topic"
                    name="topic"
                    value={formData.topic}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-600 text-sm appearance-none"
                  >
                    <option value="">Select a Topic</option>
                    {topics.map((topic, index) => (
                      <option key={index} value={topic}>{topic}</option>
                    ))}
                    <option value="custom">Custom Topic</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  placeholder="Tell us more about your event, audience, and any specific requirements..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-600 text-sm resize-none"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <Button 
                type="submit" 
                variant="primary" 
                size="lg" 
                className="w-full"
                onClick={isSubmitting ? undefined : undefined}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Speaking Inquiry'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpeakingSection;
